import { useState, useCallback } from 'react';
import { Product, ScanResult } from '../types';
import { getProductById, addProduct, updateProduct, increaseQuantity, decreaseQuantity } from '../utils/storage';
import { parseGs1Data, buildProductId } from '../utils/gs1';

interface ProductManagerState {
  currentProduct: Product | null;
  isProductFormOpen: boolean;
  isNewProduct: boolean;
  scanResult: ScanResult | null;
  isActionModalOpen: boolean;
  computedProductId?: string | null;
  initialProductData?: Partial<Product> | null;
  expiryEditable?: boolean;
}

export const useProductManager = () => {
  const [state, setState] = useState<ProductManagerState>({
    currentProduct: null,
    isProductFormOpen: false,
    isNewProduct: false,
    scanResult: null,
    isActionModalOpen: false,
    computedProductId: null,
    initialProductData: null,
    expiryEditable: true
  });

  const handleScanSuccess = useCallback((result: ScanResult) => {
    const format = result.format || '';
    const formatUpper = format.toUpperCase();
    const code = typeof result.formatCode === 'number' ? result.formatCode : (Number.isFinite(Number(format)) ? Number(format) : undefined);
    const isDataMatrix =
      code === 5 ||
      formatUpper.includes('DATA_MATRIX') ||
      formatUpper.includes('DATA MATRIX') ||
      formatUpper.includes('DATAMATRIX');
    const isEan = code === 7 || formatUpper.includes('EAN');

    if (isDataMatrix) {
      // Parse GS1 to get GTIN and expiry date
      const parsed = parseGs1Data(result.text);
      const baseCode = parsed.gtin || result.text;
      const expiry = parsed.expiryDate;
      const computedId = buildProductId(baseCode, expiry);
      const existing = expiry ? getProductById(computedId) : undefined;

      if (existing) {
        setState({
          currentProduct: existing,
          isProductFormOpen: false,
          isNewProduct: false,
          scanResult: result,
          isActionModalOpen: true,
          computedProductId: computedId,
          initialProductData: null,
          expiryEditable: true
        });
      } else {
        setState({
          currentProduct: null,
          isProductFormOpen: true,
          isNewProduct: true,
          scanResult: result,
          isActionModalOpen: false,
          computedProductId: computedId,
          initialProductData: {
            id: computedId,
            baseCode,
            barcodeFormat: format,
            expiryDate: expiry,
            quantity: 1
          },
          // Expiry date field should be editable and pre-filled
          expiryEditable: true
        });
      }
      return;
    }

    // For EAN – require manual entry of expiry date, always open form
    if (isEan) {
      setState({
        currentProduct: null,
        isProductFormOpen: true,
        isNewProduct: true,
        scanResult: result,
        isActionModalOpen: false,
        computedProductId: null,
        initialProductData: {
          id: result.text,
          baseCode: result.text,
          barcodeFormat: format,
          quantity: 1
        },
        expiryEditable: true
      });
      return;
    }

    // Other formats – behavior based on the full text as before
    const productId = result.text;
    const existingProduct = getProductById(productId);
    if (existingProduct) {
      setState({
        currentProduct: existingProduct,
        isProductFormOpen: false,
        isNewProduct: false,
        scanResult: result,
        isActionModalOpen: true,
        computedProductId: null,
        initialProductData: null,
        expiryEditable: true
      });
    } else {
      setState({
        currentProduct: null,
        isProductFormOpen: true,
        isNewProduct: true,
        scanResult: result,
        isActionModalOpen: false,
        computedProductId: null,
        initialProductData: {
          id: productId,
          baseCode: productId,
          barcodeFormat: format,
          quantity: 1
        },
        expiryEditable: true
      });
    }
  }, []);

  // Moved definition above handleProductSubmit to avoid use-before-assign
  const closeProductForm = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isProductFormOpen: false
    }));
  }, []);

  const handleProductSubmit = useCallback((productData: Omit<Product, 'dateAdded' | 'lastUpdated'>) => {
    const now = new Date().toISOString();

    if (state.isNewProduct) {
      // If DataMatrix – build ID from baseCode and (potentially changed) expiry date
      const scanFormatUpper = state.scanResult?.format?.toUpperCase() || '';
      const scanCode = typeof state.scanResult?.formatCode === 'number'
        ? state.scanResult!.formatCode
        : (Number.isFinite(Number(state.scanResult?.format)) ? Number(state.scanResult?.format) : undefined);
      const isDataMatrixScan =
        scanCode === 5 ||
        scanFormatUpper.includes('DATA_MATRIX') ||
        scanFormatUpper.includes('DATA MATRIX') ||
        scanFormatUpper.includes('DATAMATRIX');
      if (isDataMatrixScan) {
        const baseCode = productData.baseCode || productData.id;
        const finalId = buildProductId(baseCode, productData.expiryDate);
        const existing = getProductById(finalId);

        if (existing) {
          setState(prev => ({
            ...prev,
            currentProduct: existing,
            isProductFormOpen: false,
            isNewProduct: false,
            isActionModalOpen: true
          }));
          return;
        }

        const newProduct: Product = {
          ...productData,
          id: finalId,
          baseCode,
          dateAdded: now,
          lastUpdated: now
        };
        addProduct(newProduct);
        closeProductForm();
        return;
      }

      // EAN – build ID from baseCode and expiry date
      const baseCode = productData.baseCode || productData.id;
      const finalId = buildProductId(baseCode, productData.expiryDate);
      const existing = getProductById(finalId);

      if (existing) {
        // Open action modal instead of adding a duplicate
        setState(prev => ({
          ...prev,
          currentProduct: existing,
          isProductFormOpen: false,
          isNewProduct: false,
          isActionModalOpen: true
        }));
        return;
      }

      const newProduct: Product = {
        ...productData,
        id: finalId,
        baseCode,
        dateAdded: now,
        lastUpdated: now
      };
      addProduct(newProduct);
      closeProductForm();
      return;
    }

    // Editing an existing product
    const updatedProduct: Product = {
      ...productData,
      dateAdded: state.currentProduct?.dateAdded || now,
      lastUpdated: now
    };
    updateProduct(updatedProduct);
    closeProductForm();
  }, [state.currentProduct, state.isNewProduct, state.scanResult, closeProductForm]);

  const openEditForm = useCallback((product: Product) => {
    setState({
      currentProduct: product,
      isProductFormOpen: true,
      isNewProduct: false,
      scanResult: null,
      isActionModalOpen: false,
      computedProductId: product.id,
      initialProductData: product,
      expiryEditable: true
    });
  }, []);

  const closeActionModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isActionModalOpen: false
    }));
  }, []);

  const handleProductAction = useCallback((action: 'increase' | 'decrease', id: string, amount: number = 1) => {
    if (action === 'increase') {
      increaseQuantity(id, amount);
    } else {
      decreaseQuantity(id, amount);
    }
  }, []);

  return {
    ...state,
    handleScanSuccess,
    handleProductSubmit,
    openEditForm,
    closeProductForm,
    closeActionModal,
    handleProductAction
  };
};

export default useProductManager; 